import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Gift, Copy, Check, Mail } from 'lucide-react';
import { AuthContext } from '../App';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

/**
 * Invites Settings Page - LikeKind (LOCKED)
 * 
 * Per directive:
 * - 3 Invite Credits
 * - Each invite grants 3 months complimentary membership
 * - No gamification, no counters, no badges
 * - Simple, calm UI
 */
export default function InvitesSettings() {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const [invites, setInvites] = useState([]);
  const [invitesRemaining, setInvitesRemaining] = useState(3);
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState('');
  const [sending, setSending] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!user) {
      navigate('/auth');
      return;
    }
    fetchInvites();
  }, [user]);

  const fetchInvites = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/api/invites`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setInvites(res.data.invites || []);
      setInvitesRemaining(res.data.remaining || 0);
    } catch (error) {
      console.error('Failed to fetch invites:', error);
    } finally {
      setLoading(false);
    }
  };

  const sendInvite = async () => {
    if (!email.trim()) {
      toast.error('Please enter an email address');
      return;
    }

    if (invitesRemaining <= 0) {
      toast.error('No invites remaining');
      return;
    }

    setSending(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/api/invites/send`, { email: email.trim() }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Invite sent! They\'ll receive 3 months complimentary membership.');
      setEmail('');
      fetchInvites();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send invite');
    } finally {
      setSending(false);
    }
  };

  const copyInviteLink = async () => {
    const link = `${window.location.origin}/auth?invite=${user?.user_id}`;
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      toast.success('Invite link copied!');
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      toast.error('Could not copy link');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[var(--fy-background-primary)] flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[var(--lk-sage)]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--fy-background-primary)' }}>
      {/* Header */}
      <header className="sticky top-0 z-40" style={{ backgroundColor: 'var(--fy-background-primary)', borderBottom: '1px solid var(--fy-border-subtle)' }}>
        <div className="max-w-lg mx-auto px-4">
          <div className="flex items-center h-14 gap-3">
            <button onClick={() => navigate('/settings')} className="p-2 -ml-2 rounded-lg hover:opacity-70">
              <ArrowLeft className="w-5 h-5" style={{ color: 'var(--fy-text-primary)' }} />
            </button>
            <h1 className="text-lg font-bold" style={{ color: 'var(--fy-text-primary)' }}>Invites</h1>
          </div>
        </div>
      </header>

      <main className="max-w-lg mx-auto px-4 py-6 space-y-6">
        {/* Invite Credits Card */}
        <Card className="p-6" style={{ backgroundColor: 'var(--fy-surface-muted)', border: '1px solid var(--fy-border-subtle)' }}>
          <div className="flex items-center gap-3 mb-4">
            <div className="p-3 rounded-full bg-[var(--lk-sage)]/10">
              <Gift className="w-6 h-6 text-[var(--lk-sage)]" />
            </div>
            <div>
              <h2 className="font-semibold" style={{ color: 'var(--fy-text-primary)' }}>Invite Credits</h2>
              <p className="text-sm" style={{ color: 'var(--fy-text-secondary)' }}>
                {invitesRemaining} of 3 remaining
              </p>
            </div>
          </div>
          
          <p className="text-sm mb-4" style={{ color: 'var(--fy-text-secondary)' }}>
            Each invite grants the recipient 3 months of complimentary membership. 
            When someone you invite becomes a paid member, you get that invite back.
          </p>

          {/* Progress dots */}
          <div className="flex gap-2 mb-6">
            {[0, 1, 2].map(i => (
              <div 
                key={i}
                className={`w-3 h-3 rounded-full ${i < invitesRemaining ? 'bg-[var(--lk-sage)]' : 'bg-slate-300'}`}
              />
            ))}
          </div>

          {invitesRemaining > 0 ? (
            <>
              {/* Send by email */}
              <div className="mb-4">
                <label className="text-sm font-medium mb-2 block" style={{ color: 'var(--fy-text-primary)' }}>
                  Send invite by email
                </label>
                <div className="flex gap-2">
                  <Input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="friend@example.com"
                    className="flex-1"
                  />
                  <Button
                    onClick={sendInvite}
                    disabled={sending || !email.trim()}
                    className="bg-[var(--lk-sage)] text-white"
                  >
                    {sending ? '...' : <Mail className="w-4 h-4" />}
                  </Button>
                </div>
              </div>

              {/* Or copy link */}
              <div className="text-center">
                <p className="text-xs mb-2" style={{ color: 'var(--fy-text-secondary)' }}>or</p>
                <Button
                  variant="outline"
                  onClick={copyInviteLink}
                  className="w-full"
                >
                  {copied ? <Check className="w-4 h-4 mr-2" /> : <Copy className="w-4 h-4 mr-2" />}
                  {copied ? 'Copied!' : 'Copy Invite Link'}
                </Button>
              </div>
            </>
          ) : (
            <p className="text-center py-4" style={{ color: 'var(--fy-text-secondary)' }}>
              You've used all your invites. Thank you for sharing LikeKind!
            </p>
          )}
        </Card>

        {/* Sent Invites */}
        {invites.length > 0 && (
          <Card className="p-4" style={{ backgroundColor: 'var(--fy-surface-muted)', border: '1px solid var(--fy-border-subtle)' }}>
            <h3 className="font-medium mb-3" style={{ color: 'var(--fy-text-primary)' }}>Sent Invites</h3>
            <div className="space-y-3">
              {invites.map((invite, idx) => (
                <div key={idx} className="flex items-center justify-between py-2 border-b last:border-0" style={{ borderColor: 'var(--fy-border-subtle)' }}>
                  <span className="text-sm" style={{ color: 'var(--fy-text-primary)' }}>{invite.email}</span>
                  <span className={`text-xs px-2 py-0.5 rounded-full ${
                    invite.status === 'accepted' 
                      ? 'bg-green-100 text-green-700'
                      : 'bg-slate-100 text-slate-600'
                  }`}>
                    {invite.status === 'accepted' ? 'Joined' : 'Pending'}
                  </span>
                </div>
              ))}
            </div>
          </Card>
        )}
      </main>
    </div>
  );
}
